/**
 * Auth API endpoints
 */

import { get, post, put } from './client';
import type { User } from '../types';

interface MeResponse {
  logged_in: boolean;
  user: {
    id: number;
    email: string;
    name: string;
    picture?: string;
    is_admin?: boolean;
    language?: string;
  };
}

interface TestLoginResponse {
  success: boolean;
  user: {
    id: number;
    email: string;
    name: string;
  };
}

/**
 * Get current user info
 * Returns null if not authenticated (401)
 */
export async function getMe(): Promise<User | null> {
  try {
    const data = await get<MeResponse>('/api/me');
    if (!data.logged_in || !data.user) {
      return null;
    }
    return {
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      picture: data.user.picture,
      is_admin: data.user.is_admin,
      language: data.user.language || 'sr',
    };
  } catch (error) {
    // 401/403 means not authenticated
    return null;
  }
}

/**
 * Redirect to Google OAuth login
 */
export function login(): void {
  window.location.href = '/login';
}

/**
 * Logout and clear session
 */
export function logout(): void {
  window.location.href = '/logout';
}

/**
 * Test login (only works in development/test mode)
 * Used for E2E testing to bypass OAuth
 */
export async function testLogin(
  email = 'e2e-test@astrolucy.com',
  name = 'E2E Test User'
): Promise<User | null> {
  try {
    const data = await post<TestLoginResponse>('/api/test-login', {
      email,
      name,
    });
    if (data.success) {
      return {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
      };
    }
    return null;
  } catch (error) {
    console.error('Test login failed:', error);
    return null;
  }
}

/**
 * Update user profile (language preference)
 */
export async function updateProfile(data: { language: string }): Promise<{ success: boolean; language: string }> {
  return put<{ success: boolean; language: string }>('/api/profile', data);
}
